import type { ThemeConfig } from 'antd';
import { theme } from 'antd';

/**
 * The Cella design language, delivered through Ant Design tokens.
 *
 * `docs/celladesign.md` describes a Next.js + Tailwind codebase that generates
 * classes from CSS custom properties. CLAUDE.md §3 forbids Tailwind here — "Do
 * not hand-roll a design system. Do not add Tailwind on top of AntD. Use AntD
 * tokens for theming" — so the same decisions arrive by a different route:
 * `tokens.css` holds the values (and is the only file where a hex may appear),
 * and this reads them into AntD.
 *
 * Everything below is a translation of a decision the reference already made.
 * Where the two disagree, `tokens.css` and the reference win.
 */

/**
 * Reads a token at call time.
 *
 * Deliberately NOT a build-time constant. next-themes swaps a class and nothing
 * else tells you the colours moved (reference §6); the same is true of the
 * `data-theme` attribute here. Anything that needs a colour outside CSS — a
 * chart series, a canvas — must read it when it renders, or it will be showing
 * light-mode colours on a dark ground.
 */
export function token(name: string): string {
  if (typeof document === 'undefined') return '';
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

/** The four chart series. Categories, not a ramp — hardware is not further along than tags. */
export function chartSeries(): string[] {
  return ['--chart-1', '--chart-2', '--chart-3', '--chart-4'].map(token);
}

/** The volume ramp. One axis, so each step genuinely reads as further along. */
export function volumeBands(): string[] {
  return ['--band-1', '--band-2', '--band-3', '--band-4'].map(token);
}

/**
 * Six owner hues, dealt in SORTED ID ORDER rather than hashed.
 *
 * The reference is explicit about why: "a hash gave three colours to six
 * people". Dealing guarantees the first six owners are all different, which is
 * the only property that matters at badge size.
 */
export function ownerColour(index: number): string {
  return token(`--owner-${String((index % 6) + 1)}`);
}

/**
 * The type scale. Six sizes, and that is the whole scale (reference §2).
 *
 * Body is 14, table cells 13. Exported so a component asks for `TYPE.meta`
 * rather than inventing an 11.
 */
export const TYPE = {
  /** 12px — labels, hints, chips, meta lines, card descriptions. */
  meta: 12,
  /** 13px — table cells, dense UI, most buttons. */
  dense: 13,
  /** 14px — body, card titles, form inputs on desktop. */
  body: 14,
  /** 16px — form inputs below the sm breakpoint. See the iOS note in styles.css. */
  input: 16,
  /** 20px — page headings. */
  heading: 20,
  /** 28px — the single big number on a summary card. */
  display: 28,
} as const;

const SANS =
  "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";
const MONO = "'IBM Plex Mono', 'SF Mono', 'Roboto Mono', Menlo, Consolas, monospace";

/**
 * Builds the AntD config for a resolved mode.
 *
 * Called with the mode the user is actually seeing, so `document` has already
 * had `data-theme` set and `token()` reads the right side of `tokens.css`.
 */
export function buildTheme(mode: 'light' | 'dark'): ThemeConfig {
  const dark = mode === 'dark';

  return {
    algorithm: dark ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: {
      colorPrimary: token('--primary'),
      colorPrimaryHover: token('--primary-hover'),
      colorPrimaryActive: token('--primary-active'),
      colorPrimaryBg: token('--primary-soft'),
      colorPrimaryBorder: token('--primary-border'),
      colorLink: token('--primary'),

      // Status band, reserved. The primary is a violet precisely so it never
      // sits in the green/amber/red range and read as a warning on a button.
      colorSuccess: token('--positive'),
      colorWarning: token('--warning'),
      colorError: token('--negative'),
      colorInfo: token('--info'),
      colorSuccessBg: token('--positive-soft'),
      colorWarningBg: token('--warning-soft'),
      colorErrorBg: token('--negative-soft'),
      colorInfoBg: token('--info-soft'),

      colorBgLayout: token('--bg'),
      colorBgContainer: token('--surface'),
      colorBgElevated: token('--surface-raised'),
      colorFillAlter: token('--surface-sunken'),

      colorText: token('--ink'),
      colorTextSecondary: token('--ink-muted'),
      colorTextTertiary: token('--ink-subtle'),
      colorTextDescription: token('--ink-muted'),
      colorTextPlaceholder: token('--ink-subtle'),

      colorBorder: token('--line-strong'),
      colorBorderSecondary: token('--line'),

      fontFamily: SANS,
      fontFamilyCode: MONO,
      fontSize: TYPE.body,
      fontSizeSM: TYPE.dense,
      // `fontSizeLG` sizes LARGE CONTROLS, not headings. The forms use
      // `size="large"` for the 44px touch targets CLAUDE.md §10 requires on the
      // workshop and warehouse screens, so this is the font a gloved user reads
      // in an input — 16px, the same value the iOS rule in styles.css lands on.
      // It was briefly TYPE.heading, which rendered every touch input at 20px.
      // Headings are set by the fontSizeHeading* tokens below and nothing else.
      fontSizeLG: TYPE.input,
      fontSizeHeading1: TYPE.display,
      fontSizeHeading4: TYPE.heading,
      fontSizeHeading5: TYPE.body,

      // Reference §4: cards 8px, buttons and inputs rounded-md, chips rounded.
      borderRadius: 6,
      borderRadiusLG: 8,
      borderRadiusSM: 4,

      // Dense on purpose. A service advisor scanning a job list cares about
      // rows-per-screen far more than about breathing room.
      controlHeight: 32,
      // The iOS minimum, for the scanner and technician screens CLAUDE.md §10
      // says must work on a phone, in a workshop, with gloves on.
      controlHeightLG: 44,
      controlHeightSM: 28,

      // One flat shadow. Reference §4: flat fills, no gradients anywhere.
      boxShadow: token('--shadow-card'),
      boxShadowSecondary: token('--shadow-card'),
      boxShadowTertiary: token('--shadow-card'),

      wireframe: false,
    },
    components: {
      Layout: {
        headerBg: token('--surface'),
        headerHeight: 56,
        headerPadding: '0 16px',
        // The sidebar keeps the TRUE brand colour with white text, per the
        // reference's note about the solid nav pill.
        siderBg: token('--nav-bg'),
        bodyBg: token('--bg'),
      },
      Menu: {
        darkItemBg: 'transparent',
        darkSubMenuItemBg: 'transparent',
        darkItemSelectedBg: token('--brand-primary'),
        darkItemHoverBg: 'rgba(255, 255, 255, 0.08)',
        itemHeight: 36,
        fontSize: TYPE.dense,
      },
      Table: {
        headerBg: token('--surface-sunken'),
        headerColor: token('--ink-muted'),
        // 32px rows (reference §5). `cellPaddingBlock: 6` with a 13px line
        // lands there; anything larger and a dense list stops being dense.
        //
        // All THREE size variants are set, because AntD reads a different token
        // per table size and the unset ones silently keep their defaults. Every
        // list in the app renders `size="middle"` (components/ListPage.tsx), so
        // setting only the base pair left every real table at AntD's stock 49px
        // row — the density the reference asks for applied to nothing.
        cellPaddingBlock: 6,
        cellPaddingBlockMD: 6,
        cellPaddingBlockSM: 4,
        cellPaddingInline: 12,
        cellPaddingInlineMD: 12,
        cellPaddingInlineSM: 8,
        cellFontSize: TYPE.dense,
        cellFontSizeMD: TYPE.dense,
        cellFontSizeSM: TYPE.meta,
        rowHoverBg: token('--primary-soft'),
        borderColor: token('--line'),
      },
      Card: {
        paddingLG: 20,
        headerFontSize: TYPE.body,
        headerHeight: 46,
      },
      Descriptions: {
        labelBg: token('--surface-sunken'),
        titleMarginBottom: 12,
      },
      Form: { itemMarginBottom: 16, labelFontSize: TYPE.meta },
      // Tag's font size and line height are NOT settable here: antd derives
      // `tagFontSize`/`tagLineHeight` internally and ignores the values passed
      // in, even though its ComponentToken interface lists them. The chip
      // density that the table row height depends on is set in styles.css
      // instead — see the `.ant-tag` rule there.
      Tag: { defaultBg: token('--surface-sunken'), borderRadiusSM: 4 },
      Button: { fontWeight: 500, contentFontSize: TYPE.dense },
      Segmented: { itemSelectedBg: token('--surface'), trackBg: token('--surface-sunken') },
      Statistic: { contentFontSize: TYPE.display, titleFontSize: TYPE.meta },
      Alert: { withDescriptionIconSize: 20 },
    },
  };
}

/**
 * Status tones, defined once so every module renders the same state the same.
 *
 * These map to AntD's own tone names rather than to colours, which keeps the
 * status band reserved: a component asks for `good`, never for green.
 */
export const STATUS_COLOURS = {
  neutral: 'default',
  progress: 'processing',
  good: 'success',
  attention: 'warning',
  blocked: 'error',
} as const;

export type StatusTone = keyof typeof STATUS_COLOURS;
